import React, { useMemo, useState, useEffect } from 'react';
import axios from 'axios';
import useIsMobile from './useIsMobile';
import {
  MONTHS_AR,
  PAYMENT_METHODS,
  fmt,
  projLabel,
  quarterFromMonth,
  stagesForPayment,
  parseChecklist,
  checklistAnswered,
  completedCount,
  isDealClosed,
  ST_LABELS,
  DEAL_OWNER,
} from './constants';
import { getChecklistAssignees } from '../../api';

// ── DealEditModal ──
// Edit an already-registered deal. Pre-fills from the deal record and PUTs to
// /api/units-deals/deals/:id. Mirrors DealRegisterModal's field set but keeps
// the unit locked (you can't move a deal to a different unit from here — to do
// that, delete and re-register).
// Also unifies the previous UnitEditModal: when called from the Units tab on a
// reserved/sold/transferred unit, the linked unit row is passed in so the user
// can edit the pipeline stage + transfer date without leaving the modal.
export default function DealEditModal({ deal, unit, brokers, team, onClose, onSubmit, onDelete, t, lang }) {
  const isMobile = useIsMobile();

  const [form, setForm] = useState({
    listed_price: Number(deal.price || deal.actual_price || 0),
    actual_price: Number(deal.actual_price || deal.price || 0),
    channel: deal.channel || 'direct',
    broker_name: deal.broker_name || '',
    broker_pct: Number(deal.broker_pct || 2),
    closer: deal.closer || '',
    deal_month: deal.deal_month || MONTHS_AR[new Date().getMonth()],
    quarter: deal.quarter || quarterFromMonth(deal.deal_month) || 'Q1',
    earnest_money: Number(deal.earnest_money || 0),
    payment_method: PAYMENT_METHODS.some((p) => p.value === deal.payment_method) ? deal.payment_method : 'كاش',
    afrag_date: unit?.afrag_date || '',
    status: unit?.status || 'محجوز',
    // Checklist replaces deal_stage + meter toggles. Each pipeline step gets a
    // yes/no answer stored under its index ("0", "1", ...). Persisted as JSON
    // in units.deal_checklist.
    checklist: parseChecklist(unit?.deal_checklist),
    // Optional free-text comment per step (same index as checklist). Visible to everyone.
    checklistNotes: parseChecklist(unit?.deal_checklist_notes),
    note: deal.note || '',
    // Customer (lives on the unit row). Pre-fill the current buyer; allow
    // re-picking an existing lead or typing a new customer — same as register.
    customer_mode: 'existing',
    client_name: unit?.client_name || '',
    client_phone: unit?.client_phone || '',
    lead_id: null,
  });
  const u = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  // Deal-ready leads for re-linking the buyer to a CRM lead (mirrors register).
  const [leads, setLeads] = useState([]);
  const [leadsLoading, setLeadsLoading] = useState(false);
  const [leadSearch, setLeadSearch] = useState('');
  useEffect(() => {
    let cancelled = false;
    setLeadsLoading(true);
    (async () => {
      try {
        const statuses = ['interested', 'site_visit_scheduled', 'offer_sent', 'negotiating', 'won'];
        const results = await Promise.all(
          statuses.map((s) => axios.get('/api/leads', { params: { status: s, limit: 200 } }).then((r) => r.data?.leads || []).catch(() => []))
        );
        if (cancelled) return;
        const byId = new Map();
        [].concat(...results).forEach((l) => { byId.set(l.id, l); });
        setLeads(Array.from(byId.values()).sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)));
      } catch (e) {
        if (!cancelled) setLeads([]);
      } finally {
        if (!cancelled) setLeadsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const filteredLeads = useMemo(() => {
    const q = leadSearch.trim().toLowerCase();
    if (!q) return leads.slice(0, 50);
    return leads
      .filter((l) => (l.name || '').toLowerCase().includes(q) || (l.phone || '').toLowerCase().includes(q))
      .slice(0, 50);
  }, [leads, leadSearch]);

  const pickLead = (l) => {
    setForm((p) => ({ ...p, customer_mode: 'existing', client_name: l.name || '', client_phone: l.phone || '', lead_id: l.id }));
    setLeadSearch('');
  };
  const clearLead = () => setForm((p) => ({ ...p, lead_id: null, client_name: '', client_phone: '' }));
  const setAnswer = (idx, value) =>
    setForm((p) => ({ ...p, checklist: { ...p.checklist, [idx]: value } }));
  const setStepNote = (idx, text) =>
    setForm((p) => ({ ...p, checklistNotes: { ...p.checklistNotes, [idx]: text } }));

  const listedPrice = Number(form.listed_price) || 0;
  const price = Number(form.actual_price) || 0;
  const priceChanged = price !== listedPrice;
  const needsBroker = form.channel === 'broker' || form.channel === 'mixed';
  const brokerOnly = form.channel === 'broker';

  // Pipeline driven by payment method. Cash and bank have different orderings
  // (bank inserts a "set transfer location" step before الأفراغ).
  const stages = stagesForPayment(form.payment_method);
  const done = completedCount(stages, form.checklist);
  const closed = isDealClosed(stages, form.checklist);

  // Company-wide per-step assignees (Settings → deal-closure tasks). __deal_owner__
  // resolves to the deal's own closer.
  const [checklistAssignees, setChecklistAssignees] = useState({});
  useEffect(() => { getChecklistAssignees().then(r => setChecklistAssignees(r.assignees || {})); }, []);
  const assigneeFor = (stage) => {
    const raw = checklistAssignees[stage.en];
    if (!raw) return '';
    return raw === DEAL_OWNER ? (form.closer || (lang === 'ar' ? 'صاحب الصفقة' : 'Deal owner')) : raw;
  };

  const brokerCommission = useMemo(() => {
    if (!needsBroker) return 0;
    return Math.round((price * Number(form.broker_pct || 0)) / 100);
  }, [price, form.broker_pct, needsBroker]);

  const validationError = useMemo(() => {
    if (!form.client_name || !form.client_name.trim()) return lang === 'ar' ? 'اسم العميل مطلوب' : 'Customer name required';
    if (!brokerOnly && !form.closer) return lang === 'ar' ? 'اختر منفّذ الصفقة' : 'Select a closer';
    if (needsBroker && !form.broker_name) return lang === 'ar' ? 'اختر الوسيط' : 'Select a broker';
    if (price <= 0) return lang === 'ar' ? 'السعر غير محدد' : 'Price not set';
    if (listedPrice <= 0) return lang === 'ar' ? 'السعر الأصلي غير محدد' : 'Listed price not set';
    return null;
  }, [form, needsBroker, brokerOnly, price, listedPrice, lang]);

  const submit = () => {
    if (validationError) return;
    // Guard against silent earnest edits: changing العربون requires an explicit
    // confirmation before persisting. Other field edits save without a prompt.
    const beforeEarnest = Number(deal.earnest_money || 0);
    const afterEarnest = Number(form.earnest_money || 0);
    if (afterEarnest !== beforeEarnest) {
      const msg = lang === 'ar'
        ? `تغيير العربون من ${beforeEarnest.toLocaleString('en-US')} إلى ${afterEarnest.toLocaleString('en-US')} ريال.\nهل أنت متأكد؟`
        : `Change earnest from ${beforeEarnest.toLocaleString('en-US')} to ${afterEarnest.toLocaleString('en-US')} SAR.\nAre you sure?`;
      if (!window.confirm(msg)) return;
    }
    onSubmit(deal.id, {
      price: listedPrice,
      actual_price: price,
      // (listed price is now editable; server stores the new value as `price`)
      channel: form.channel,
      broker_name: needsBroker ? form.broker_name : '',
      broker_pct: needsBroker ? Number(form.broker_pct || 0) : 0,
      closer: brokerOnly ? '' : form.closer,
      deal_month: form.deal_month,
      quarter: form.quarter,
      earnest_money: Number(form.earnest_money || 0),
      payment_method: form.payment_method,
      // deal_stage kept for legacy callers / reports — derive from the checklist
      // so old views that show "stage X" still increment as steps get answered.
      deal_stage: done,
      afrag_date: form.afrag_date || '',
      status: form.status,
      deal_checklist: JSON.stringify(form.checklist || {}),
      deal_checklist_notes: JSON.stringify(form.checklistNotes || {}),
      note: (form.note || '').trim(),
      client_name: (form.client_name || '').trim(),
      client_phone: (form.client_phone || '').trim(),
      lead_id: form.lead_id || null,
    });
  };

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && !validationError) submit(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [validationError, form, submit]);

  return (
    <div className="crm-modal-overlay" onClick={onClose}>
      <div
        className="crm-modal"
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: 560,
          maxHeight: isMobile ? '100vh' : '92vh',
          display: 'flex',
          flexDirection: 'column',
          padding: 0,
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          <div>
            <div style={{ fontSize: '0.62rem', color: 'var(--text-secondary)', letterSpacing: 0.4, marginBottom: 2, textTransform: 'uppercase' }}>
              {lang === 'ar' ? 'تعديل الصفقة' : 'Edit Deal'}
            </div>
            <h2 style={{ fontSize: '1.05rem', fontWeight: 800, margin: 0 }}>
              {projLabel(deal.project, t)} — {deal.unit_code}
            </h2>
          </div>
          <button className="btn btn-secondary btn-sm" onClick={onClose}>&times;</button>
        </div>

        {/* Body */}
        <div style={{ overflowY: 'auto', flex: 1, padding: '14px 16px' }}>
          {/* Deal price — two editable fields: original (السعر الأصلي / listed)
              and actual (سعر الصفقة). Discount shown live when they differ. */}
          <div
            style={{
              border: '1.5px solid ' + (priceChanged ? '#f59e0b' : 'var(--accent)'),
              background: (priceChanged ? '#f59e0b' : 'var(--accent)') + '10',
              borderRadius: 10,
              padding: '10px 12px',
              marginBottom: 14,
            }}
          >
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <PriceField
                label={lang === 'ar' ? 'السعر الأصلي' : 'Listed Price'}
                value={form.listed_price}
                onChange={(v) => u('listed_price', v)}
                accent="var(--text-secondary)"
                lang={lang}
              />
              <PriceField
                label={lang === 'ar' ? 'سعر الصفقة' : 'Deal Price'}
                value={form.actual_price}
                onChange={(v) => u('actual_price', v)}
                accent={priceChanged ? '#f59e0b' : 'var(--accent)'}
                bold
                lang={lang}
              />
            </div>
            {priceChanged && listedPrice > 0 && (
              <div
                style={{
                  marginTop: 8,
                  paddingTop: 8,
                  borderTop: '1px dashed rgba(245,158,11,0.4)',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  fontSize: '0.72rem',
                  fontWeight: 700,
                }}
              >
                <span style={{ color: 'var(--text-secondary)' }}>
                  {price < listedPrice
                    ? (lang === 'ar' ? 'الخصم' : 'Discount')
                    : (lang === 'ar' ? 'زيادة على السعر الأصلي' : 'Markup vs listed')}
                </span>
                <span style={{ direction: 'ltr', color: '#f59e0b' }}>
                  {price < listedPrice ? '-' : '+'}{fmt(Math.abs(listedPrice - price))} SAR
                  <span style={{ color: 'var(--text-secondary)', marginInlineStart: 6, fontWeight: 600 }}>
                    ({Math.round((Math.abs(listedPrice - price) / listedPrice) * 100)}%)
                  </span>
                </span>
              </div>
            )}
          </div>

          {/* Customer — always an existing lead */}
          <SectionLabel>{lang === 'ar' ? 'العميل' : 'Customer'} <span style={{ color: '#ef4444' }}>*</span></SectionLabel>

          {form.customer_mode === 'existing' ? (
            <div style={{ marginBottom: 14 }}>
              {(form.lead_id || form.client_name) ? (
                <div style={{ background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.35)', borderRadius: 8, padding: '8px 10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: '0.86rem', fontWeight: 800 }}>{form.client_name}
                      {!form.lead_id && <span style={{ fontSize: '0.62rem', fontWeight: 600, color: 'var(--text-secondary)', marginInlineStart: 6 }}>{lang === 'ar' ? '\u00b7 \u0645\u0646 \u0627\u0644\u062d\u062c\u0632' : '\u00b7 from reservation'}</span>}
                    </div>
                    <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', direction: 'ltr' }}>{form.client_phone || '\u2014'}</div>
                  </div>
                  <button type="button" onClick={clearLead} style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 6, padding: '4px 10px', fontSize: '0.7rem', cursor: 'pointer', fontWeight: 700 }}>
                    {lang === 'ar' ? 'تغيير' : 'Change'}
                  </button>
                </div>
              ) : (
                <>
                  <input
                    type="text"
                    placeholder={lang === 'ar' ? 'ابحث بالاسم أو الجوال…' : 'Search name or phone…'}
                    value={leadSearch}
                    onChange={(e) => setLeadSearch(e.target.value)}
                    style={{ width: '100%', padding: '9px 11px', borderRadius: 8, border: '1px solid var(--border)', fontSize: '0.86rem', background: 'var(--bg-card)', boxSizing: 'border-box', marginBottom: 6 }}
                  />
                  <div style={{ maxHeight: 180, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 8, background: 'var(--bg-card)' }}>
                    {leadsLoading ? (
                      <div style={{ padding: 16, textAlign: 'center', fontSize: '0.78rem', color: 'var(--text-secondary)' }}>{lang === 'ar' ? 'جارٍ التحميل…' : 'Loading…'}</div>
                    ) : filteredLeads.length === 0 ? (
                      <div style={{ padding: 16, textAlign: 'center', fontSize: '0.78rem', color: 'var(--text-secondary)' }}>{lang === 'ar' ? 'لا توجد عملاء مطابقين' : 'No matching leads'}</div>
                    ) : (
                      filteredLeads.map((l, i) => (
                        <button
                          key={l.id}
                          type="button"
                          onClick={() => pickLead(l)}
                          style={{ width: '100%', padding: '8px 10px', background: 'transparent', border: 'none', borderBottom: i < filteredLeads.length - 1 ? '1px solid var(--border)' : 'none', cursor: 'pointer', textAlign: lang === 'ar' ? 'right' : 'left', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}
                          onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--bg-primary)')}
                          onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                        >
                          <div style={{ minWidth: 0, flex: 1 }}>
                            <div style={{ fontSize: '0.84rem', fontWeight: 700 }}>{l.name}</div>
                            <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', direction: 'ltr' }}>{l.phone || '\u2014'} {l.assignedTo ? '\u00b7 ' + l.assignedTo : ''}</div>
                          </div>
                          <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: '0.62rem', fontWeight: 700, background: l.status === 'won' ? 'rgba(16,185,129,0.18)' : 'rgba(99,102,241,0.14)', color: l.status === 'won' ? '#10b981' : 'var(--accent)' }}>{l.status}</span>
                        </button>
                      ))
                    )}
                  </div>
                </>
              )}
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 8, marginBottom: 14 }}>
              <div className="input-group">
                <label>{lang === 'ar' ? 'اسم العميل' : 'Customer name'}</label>
                <input type="text" value={form.client_name} onChange={(e) => u('client_name', e.target.value)} placeholder={lang === 'ar' ? 'الاسم الكامل' : 'Full name'} />
              </div>
              <div className="input-group">
                <label>{lang === 'ar' ? 'الجوال' : 'Phone'}</label>
                <input type="tel" value={form.client_phone} onChange={(e) => u('client_phone', e.target.value)} placeholder="05XXXXXXXX" style={{ direction: 'ltr' }} />
              </div>
            </div>
          )}

          {/* Channel */}
          <SectionLabel>{t('udSalesChannel')}</SectionLabel>
          <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
            {[
              { k: 'direct', l: t('udDirect') },
              { k: 'broker', l: t('udBroker') },
              { k: 'mixed', l: t('udMixed') },
            ].map((ch) => (
              <button
                key={ch.k}
                onClick={() => u('channel', ch.k)}
                style={{
                  flex: 1,
                  padding: '10px 6px',
                  borderRadius: 8,
                  border: form.channel === ch.k ? '1.5px solid var(--accent)' : '1.5px solid var(--border)',
                  background: form.channel === ch.k ? 'rgba(99,102,241,0.08)' : 'transparent',
                  color: form.channel === ch.k ? 'var(--accent)' : 'var(--text-secondary)',
                  fontSize: '0.82rem',
                  fontWeight: 700,
                  cursor: 'pointer',
                  minHeight: 44,
                }}
              >
                {ch.l}
              </button>
            ))}
          </div>

          {/* Broker */}
          {needsBroker && (
            <div style={{ background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.25)', borderRadius: 10, padding: 12, marginBottom: 14 }}>
              <SectionLabel color="#f59e0b">{lang === 'ar' ? 'تفاصيل الوسيط' : 'Broker Details'}</SectionLabel>
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 8, marginBottom: 8 }}>
                <div className="input-group">
                  <label>{t('udBrokerName')}</label>
                  <select value={form.broker_name} onChange={(e) => {
                    const b = brokers.find((x) => x.name === e.target.value);
                    setForm((p) => ({ ...p, broker_name: e.target.value, broker_pct: b?.default_pct || p.broker_pct }));
                  }}>
                    <option value="">— {lang === 'ar' ? 'اختر' : 'Select'} —</option>
                    {brokers.map((b) => (<option key={b.id} value={b.name}>{b.name} ({b.default_pct}%)</option>))}
                  </select>
                </div>
                <div className="input-group">
                  <label>{t('udBrokerPct')}</label>
                  <input type="number" value={form.broker_pct} onChange={(e) => u('broker_pct', e.target.value)} min="0.5" max="10" step="0.1" />
                </div>
              </div>
              <div style={{ background: '#fff', borderRadius: 6, padding: '6px 10px', fontSize: '0.74rem', display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: 'var(--text-secondary)' }}>{lang === 'ar' ? 'عمولة الوسيط' : 'Broker commission'}</span>
                <span style={{ direction: 'ltr', fontWeight: 800, color: '#f59e0b' }}>{fmt(brokerCommission)} SAR</span>
              </div>
            </div>
          )}

          {/* Closer chips — hidden when pure-broker channel (closer N/A) */}
          {!brokerOnly && (
            <>
              <SectionLabel>{t('udCloser')}</SectionLabel>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
                {team.length === 0 ? (
                  <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
                    {lang === 'ar' ? 'لا يوجد أعضاء في الفريق' : 'No team members configured'}
                  </div>
                ) : team.map((m) => (
                  <button
                    key={m}
                    onClick={() => u('closer', m)}
                    style={{
                      padding: '10px 14px',
                      borderRadius: 22,
                      border: form.closer === m ? '1.5px solid var(--accent)' : '1.5px solid var(--border)',
                      background: form.closer === m ? 'var(--accent)' : 'transparent',
                      color: form.closer === m ? '#fff' : 'var(--text-primary)',
                      fontSize: '0.82rem',
                      fontWeight: 700,
                      cursor: 'pointer',
                      minHeight: 38,
                    }}
                  >
                    {m}
                  </button>
                ))}
              </div>
            </>
          )}

          {/* Period */}
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 8, marginBottom: 10 }}>
            <div className="input-group">
              <label>{t('udMonth')}</label>
              <select
                value={form.deal_month}
                onChange={(e) => {
                  const m = e.target.value;
                  setForm((p) => ({ ...p, deal_month: m, quarter: quarterFromMonth(m) }));
                }}
              >
                {MONTHS_AR.map((m) => (<option key={m} value={m}>{m}</option>))}
              </select>
            </div>
            <div className="input-group">
              <label>{t('udQuarter')}</label>
              <input type="text" value={form.quarter} readOnly tabIndex={-1}
                style={{ background: 'var(--bg-primary)', color: 'var(--text-secondary)', fontWeight: 700, cursor: 'not-allowed' }} />
            </div>
          </div>

          {/* Earnest */}
          <div className="input-group" style={{ marginBottom: 12 }}>
            <label>{t('udEarnest')} (SAR)</label>
            <input type="number" value={form.earnest_money} onChange={(e) => u('earnest_money', e.target.value)} min="0" />
          </div>

          {/* طريقة الشراء — toggles which closure pipeline (cash 10 steps vs
              bank 11 steps) is shown below. Surfaced as big buttons because
              this is the one decision that reshapes the rest of the modal. */}
          <SectionLabel>{lang === 'ar' ? 'طريقة الشراء' : 'Purchase Method'}</SectionLabel>
          <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
            {PAYMENT_METHODS.map((p) => (
              <button
                key={p.value}
                type="button"
                onClick={() => u('payment_method', p.value)}
                style={{
                  flex: 1,
                  padding: '12px 8px',
                  borderRadius: 10,
                  border: form.payment_method === p.value ? '1.5px solid var(--accent)' : '1.5px solid var(--border)',
                  background: form.payment_method === p.value ? 'var(--accent)' : 'transparent',
                  color: form.payment_method === p.value ? '#fff' : 'var(--text-primary)',
                  fontSize: '0.92rem',
                  fontWeight: 800,
                  cursor: 'pointer',
                  minHeight: 46,
                }}
              >
                {p[lang]}
              </button>
            ))}
          </div>

          {/* Deal closure checklist — each step is a Yes/No question. Deal is
              considered "صفقة مغلقة" once every non-optional step is "yes" and
              every optional step has any answer. Pipeline shape depends on
              payment method (cash vs bank). */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 8,
            }}
          >
            <SectionLabel>
              {lang === 'ar'
                ? `إغلاق الصفقة (${form.payment_method === 'بنك' ? 'بنك' : 'كاش'})`
                : `Deal Closure (${form.payment_method === 'بنك' ? 'Bank' : 'Cash'})`}
            </SectionLabel>
            <span style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', fontWeight: 700 }}>
              {lang === 'ar'
                ? `${done} من ${stages.length} مكتملة`
                : `${done} of ${stages.length} done`}
            </span>
          </div>

          {closed && (
            <div
              style={{
                background: 'linear-gradient(135deg, #22c55e, #16a34a)',
                color: '#fff',
                borderRadius: 10,
                padding: '12px 14px',
                marginBottom: 10,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                fontWeight: 800,
                fontSize: '1rem',
                letterSpacing: 0.3,
                boxShadow: '0 4px 12px rgba(34,197,94,0.25)',
              }}
            >
              <span style={{ fontSize: '1.2rem' }}>{'\u2713'}</span>
              {lang === 'ar' ? 'صفقة مغلقة' : 'Deal Closed'}
            </div>
          )}

          <div
            style={{
              border: '1px solid var(--border)',
              borderRadius: 10,
              padding: 6,
              marginBottom: 12,
              background: 'var(--bg-card)',
            }}
          >
            {stages.map((s, i) => (
              <ChecklistRow
                key={i}
                idx={i}
                stage={s}
                value={form.checklist[i]}
                onChange={(v) => setAnswer(i, v)}
                note={form.checklistNotes[i] || ''}
                onNote={(t) => setStepNote(i, t)}
                lang={lang}
                assignee={assigneeFor(s)}
              />
            ))}
          </div>

          {/* Unit status — move the unit محجوز → مباع → أفرغ straight from the
              deal update, instead of going to the Units tab / Closure button. */}
          <div className="input-group" style={{ marginBottom: 12 }}>
            <label>{lang === 'ar' ? 'حالة الوحدة' : 'Unit Status'}</label>
            <select value={form.status} onChange={(e) => u('status', e.target.value)}>
              {['محجوز', 'مباع', 'أفرغ'].map((s) => (
                <option key={s} value={s}>{lang === 'ar' ? (ST_LABELS[s]?.ar || s) : (ST_LABELS[s]?.en || s)}</option>
              ))}
            </select>
          </div>

          {/* Transfer date */}
          <div className="input-group" style={{ marginBottom: 12 }}>
            <label>{lang === 'ar' ? 'تاريخ الأفراغ' : 'Transfer Date'}</label>
            <input
              value={form.afrag_date}
              onChange={(e) => u('afrag_date', e.target.value)}
              placeholder="DD-MM-YYYY"
            />
          </div>

          {/* Deal note — free-form context captured at registration, editable later. */}
          <div className="input-group" style={{ marginBottom: 4 }}>
            <label>{lang === 'ar' ? 'ملاحظة (اختياري)' : 'Note (optional)'}</label>
            <textarea
              value={form.note}
              onChange={(e) => u('note', e.target.value)}
              placeholder={lang === 'ar' ? 'تفاصيل عن العميل أو الصفقة…' : 'Context about the client or deal…'}
              rows={3}
              maxLength={2000}
              style={{
                width: '100%',
                padding: '9px 11px',
                borderRadius: 8,
                border: '1px solid var(--border)',
                fontSize: '0.86rem',
                background: 'var(--bg-card)',
                boxSizing: 'border-box',
                resize: 'vertical',
                fontFamily: 'inherit',
              }}
            />
          </div>
        </div>

        {/* Sticky footer */}
        <div style={{ padding: '10px 16px', borderTop: '1px solid var(--border)', background: 'var(--bg-card)', display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
            {onDelete && (
              <button
                type="button"
                onClick={() => onDelete(deal.id)}
                className="btn btn-secondary"
                style={{ padding: '10px 14px', color: '#ef4444', borderColor: 'rgba(239,68,68,0.4)', fontWeight: 800, whiteSpace: 'nowrap' }}
              >
                {lang === 'ar' ? 'حذف الصفقة' : 'Delete deal'}
              </button>
            )}
            {validationError ? (
              <span style={{ fontSize: '0.78rem', color: '#ef4444', fontWeight: 700 }}>{validationError}</span>
            ) : (
              <span style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
                {lang === 'ar' ? 'سيتم تحديث الصفقة' : 'Will update this deal'}
              </span>
            )}
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button onClick={onClose} className="btn btn-secondary" style={{ padding: '10px 14px' }}>
              {lang === 'ar' ? 'إلغاء' : 'Cancel'}
            </button>
            <button onClick={submit} disabled={!!validationError} className="btn btn-primary"
              style={{ padding: '10px 18px', fontWeight: 800, fontSize: '0.9rem', minHeight: 44, opacity: validationError ? 0.5 : 1 }}>
              {lang === 'ar' ? 'حفظ التعديلات' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function PriceField({ label, value, onChange, accent, bold, lang }) {
  return (
    <div>
      <label
        style={{
          display: 'block',
          fontSize: '0.66rem',
          fontWeight: 800,
          color: accent,
          letterSpacing: 0.3,
          textTransform: 'uppercase',
          marginBottom: 4,
        }}
      >
        {label}
      </label>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <input
          type="number"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          min="0"
          step="1000"
          style={{
            flex: 1,
            minWidth: 0,
            padding: '9px 10px',
            borderRadius: 8,
            border: '1px solid var(--border)',
            fontSize: bold ? '1.05rem' : '0.95rem',
            fontWeight: bold ? 800 : 700,
            background: '#fff',
            direction: 'ltr',
            textAlign: lang === 'ar' ? 'right' : 'left',
          }}
        />
        <span style={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--text-secondary)' }}>SAR</span>
      </div>
    </div>
  );
}

function SectionLabel({ children, color }) {
  return (
    <div style={{
      fontSize: '0.68rem',
      fontWeight: 800,
      color: color || 'var(--text-secondary)',
      letterSpacing: 0.5,
      textTransform: 'uppercase',
      marginBottom: 6,
    }}>
      {children}
    </div>
  );
}

// One row in the deal-closure checklist. Each step is a Yes/No question with
// tri-state value (empty = unanswered). Required steps show the row in green
// only when "yes". Optional steps go green for either yes/no, since either
// answer means the step has been addressed.
function ChecklistRow({ idx, stage, value, onChange, note, onNote, lang, assignee }) {
  const answered = checklistAnswered(stage, value);
  const opts = [
    { v: 'yes', l: lang === 'ar' ? 'نعم' : 'Yes', bg: '#22c55e' },
    { v: 'no', l: lang === 'ar' ? 'لا' : 'No', bg: '#ef4444' },
  ];
  return (
    <div
      style={{
        padding: '10px 10px',
        borderBottom: '1px solid var(--border)',
        background: answered ? 'rgba(34,197,94,0.06)' : 'transparent',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 }}>
        <div
          style={{
            width: 24,
            height: 24,
            borderRadius: '50%',
            background: answered ? '#22c55e' : 'var(--bg-primary)',
            color: answered ? '#fff' : 'var(--text-secondary)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '0.72rem',
            fontWeight: 800,
            border: answered ? 'none' : '1.5px solid var(--border)',
            flexShrink: 0,
          }}
        >
          {answered ? '\u2713' : idx + 1}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <span style={{ fontSize: '0.84rem', fontWeight: 600, color: 'var(--text-primary)', lineHeight: 1.3 }}>
            {stage[lang]}
            {stage.optional && (
              <span style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', marginInlineStart: 6, fontWeight: 500 }}>
                {lang === 'ar' ? '(اختياري)' : '(optional)'}
              </span>
            )}
          </span>
          {assignee && (
            <span style={{ fontSize: '0.68rem', color: 'var(--accent)', fontWeight: 700, marginTop: 2 }}>
              {'👤 '}{assignee}
            </span>
          )}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
        {opts.map((o) => (
          <button
            key={o.v}
            type="button"
            onClick={() => onChange(value === o.v ? '' : o.v)}
            style={{
              padding: '6px 14px',
              borderRadius: 16,
              border: value === o.v ? '1.5px solid ' + o.bg : '1px solid var(--border)',
              background: value === o.v ? o.bg : 'transparent',
              color: value === o.v ? '#fff' : 'var(--text-secondary)',
              fontSize: '0.78rem',
              fontWeight: 700,
              cursor: 'pointer',
              minHeight: 32,
            }}
          >
            {o.l}
          </button>
        ))}
      </div>
      </div>
      {/* Optional comment on this step — saved on the deal, visible to everyone. */}
      <input
        type="text"
        value={note}
        onChange={(e) => onNote(e.target.value)}
        placeholder={lang === 'ar' ? '💬 أضف تعليقًا (اختياري)' : '💬 Add a comment (optional)'}
        style={{
          width: '100%',
          marginTop: 8,
          padding: '6px 10px',
          borderRadius: 8,
          border: '1px solid var(--border)',
          background: 'var(--bg-primary)',
          color: 'var(--text-primary)',
          fontSize: '0.78rem',
          boxSizing: 'border-box',
        }}
      />
    </div>
  );
}
